import { useState } from "react";
import { Link, useParams } from "wouter";
import { useQuery } from "@tanstack/react-query";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend,
} from "recharts";
import { PREBUILT_TRACKERS } from "@macroscope/shared";
import { fetchGeo, fetchTracker, fetchCustomGeo, type TrackerResponse } from "../lib/api";

export default function Geopolitics() {
  const { id } = useParams<{ id?: string }>();
  const { data, isLoading } = useQuery({ queryKey: ["geo"], queryFn: fetchGeo });
  const { data: detail, isLoading: detailLoading } = useQuery({
    queryKey: ["geo", "tracker", id],
    queryFn: () => fetchTracker(id!),
    enabled: !!id,
  });

  if (isLoading) return <div className="text-sm text-slate-500">Loading Geopolitical Radar…</div>;

  const trackers = data?.trackers ?? [];
  const selected = id
    ? detailLoading
      ? undefined
      : detail ?? trackers.find((t) => PREBUILT_TRACKERS.find((d) => d.scope === t.scope)?.id === id)
    : undefined;

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <section className="space-y-2">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-lg font-semibold">Geopolitical Radar</h1>
            <p className="max-w-2xl text-xs text-slate-500">
              Live tension layer from GDELT: attention (volume), negative tone, and recent acceleration for major geopolitical themes. Links out to sources — never full article text.
            </p>
          </div>
          <CustomQueryBuilder />
        </div>
      </section>

      {selected ? (
        <TrackerDetail tracker={selected} all={trackers} />
      ) : (
        <TrackerGrid trackers={trackers} />
      )}

      <div className="text-[10px] text-slate-600">
        Source: {data?.source} · {data?.updated_at ? new Date(data.updated_at).toLocaleString() : ""}
        {data?.delayed && " · delayed"} · educational
      </div>
    </div>
  );
}

function TrackerGrid({ trackers }: { trackers: TrackerResponse[] }) {
  return (
    <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {PREBUILT_TRACKERS.map((def) => {
        const tracker = trackers.find((t) => t.scope === def.scope);
        return (
          <Link
            key={def.id}
            href={`/geopolitics/${def.id}`}
            className="rounded-xl border border-surface-2 bg-surface-1 p-4 transition-colors hover:border-macro-accent/30"
          >
            <TrackerCardContent tracker={tracker} def={def} />
          </Link>
        );
      })}
    </section>
  );
}

function TrackerCardContent({
  tracker,
  def,
}: {
  tracker?: TrackerResponse;
  def: { scope: string; affected: string[] };
}) {
  const value = tracker?.value ?? 0;
  const trend = tracker?.trend ?? "stable";
  const gap = tracker?.coverage_gap;
  return (
    <div className="space-y-3">
      <div className="flex items-start justify-between">
        <div className="text-sm font-medium">{def.scope}</div>
        {tracker && (
          <div
            className={`rounded px-2 py-0.5 text-[10px] font-medium uppercase ${
              trend === "rising"
                ? "bg-macro-down/10 text-macro-down"
                : trend === "falling"
                  ? "bg-macro-up/10 text-macro-up"
                  : "bg-surface-2 text-slate-400"
            }`}
          >
            {trend}
          </div>
        )}
      </div>

      <div className="flex items-end gap-2">
        <div className="font-mono text-3xl font-semibold">{value.toFixed(0)}</div>
        <div className="pb-1 text-xs text-slate-400">/100 tension</div>
      </div>

      {gap ? (
        <div className="text-xs text-yellow-500">Coverage gap: {gap}</div>
      ) : (
        <div className="text-xs text-slate-500">
          so-what: {tensionRead(value, trend)}
        </div>
      )}

      <div className="flex flex-wrap gap-1">
        {def.affected.map((asset) => (
          <span key={asset} className="rounded bg-surface-2 px-1.5 py-0.5 text-[10px] text-slate-400">
            {asset}
          </span>
        ))}
      </div>
    </div>
  );
}

function tensionRead(value: number, trend: string): string {
  if (value >= 70 && trend === "rising") return "escalation risk is elevated — safe-haven assets may draw bids.";
  if (value >= 70) return "elevated tension — monitor headline risk for affected assets.";
  if (value >= 40) return "moderate tension — a catalyst could sharpen moves.";
  return "relatively quiet — not the dominant macro driver right now.";
}

function TrackerDetail({ tracker, all }: { tracker: TrackerResponse; all: TrackerResponse[] }) {
  const def = PREBUILT_TRACKERS.find((d) => d.scope === tracker.scope);
  const chartData = buildChartData(tracker);

  return (
    <div className="space-y-6">
      <Link
        href="/geopolitics"
        className="inline-flex items-center gap-1 text-xs text-macro-accent hover:underline"
      >
        ← Back to geopolitical radar
      </Link>

      <div className="rounded-xl border border-surface-2 bg-surface-1 p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold">{tracker.scope}</h2>
            <p className="text-xs text-slate-500">Tension score · {tracker.value.toFixed(0)}/100 · {tracker.trend}</p>
          </div>
          <div
            className={`rounded px-3 py-1 text-xs font-medium uppercase ${
              tracker.trend === "rising"
                ? "bg-macro-down/10 text-macro-down"
                : tracker.trend === "falling"
                  ? "bg-macro-up/10 text-macro-up"
                  : "bg-surface-2 text-slate-400"
            }`}
          >
            {tracker.trend}
          </div>
        </div>

        <p className="mt-3 text-sm text-slate-400">so-what: {tensionRead(tracker.value, tracker.trend)}</p>

        {def && (
          <div className="mt-3 flex flex-wrap gap-1">
            {def.affected.map((asset) => (
              <span key={asset} className="rounded bg-surface-2 px-2 py-0.5 text-xs text-slate-400">
                {asset}
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-xl border border-surface-2 bg-surface-1 p-5">
          <h3 className="mb-4 text-xs font-medium uppercase tracking-wider text-slate-400">30-day tone & volume</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid stroke="#1f232c" strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={{ fill: "#64748b", fontSize: 10 }} angle={-45} textAnchor="end" height={50} />
                <YAxis yAxisId="left" tick={{ fill: "#64748b", fontSize: 10 }} domain={["auto", "auto"]} />
                <YAxis yAxisId="right" orientation="right" tick={{ fill: "#64748b", fontSize: 10 }} domain={["auto", "auto"]} />
                <Tooltip
                  contentStyle={{ backgroundColor: "#111318", borderColor: "#1f232c" }}
                  itemStyle={{ fontSize: 12 }}
                  labelStyle={{ color: "#94a3b8" }}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Line yAxisId="left" type="monotone" dataKey="tone" name="Tone" stroke="#ef4444" dot={false} strokeWidth={2} />
                <Line yAxisId="right" type="monotone" dataKey="volume" name="Volume" stroke="#3b82f6" dot={false} strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="rounded-xl border border-surface-2 bg-surface-1 p-5">
          <h3 className="mb-4 text-xs font-medium uppercase tracking-wider text-slate-400">Recent stories</h3>
          <StoryFeed events={tracker.top_events} />
        </div>
      </div>

      <div className="rounded-xl border border-surface-2 bg-surface-1 p-5">
        <h3 className="mb-4 text-xs font-medium uppercase tracking-wider text-slate-400">All trackers at a glance</h3>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
          {all.map((t) => {
            const def = PREBUILT_TRACKERS.find((d) => d.scope === t.scope);
            return (
              <Link
                key={t.scope}
                href={`/geopolitics/${def?.id ?? ""}`}
                className={`rounded-lg border p-3 transition-colors ${
                  t.scope === tracker.scope ? "border-macro-accent bg-macro-accent/10" : "border-surface-2 bg-surface-2 hover:border-macro-accent/30"
                }`}
              >
                <div className="text-[10px] text-slate-400">{t.scope}</div>
                <div className="font-mono text-lg font-semibold">{t.value.toFixed(0)}</div>
                <div className="text-[10px] text-slate-500">{t.trend}</div>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function buildChartData(tracker: TrackerResponse): Array<{ date: string; tone: number | null; volume: number | null }> {
  const dates = new Set<string>();
  tracker.tone_series.forEach((p) => dates.add(p.date));
  tracker.vol_series.forEach((p) => dates.add(p.date));
  return Array.from(dates)
    .sort()
    .map((date) => ({
      date: `${date.slice(4, 6)}/${date.slice(6, 8)}`,
      tone: tracker.tone_series.find((p) => p.date === date)?.value ?? null,
      volume: tracker.vol_series.find((p) => p.date === date)?.value ?? null,
    }));
}

function StoryFeed({ events }: { events: TrackerResponse["top_events"] }) {
  if (events.length === 0) {
    return <div className="text-sm text-slate-500">No recent stories available.</div>;
  }

  return (
    <ul className="space-y-3">
      {events.map((e, i) => (
        <li key={`${e.url}-${i}`} className="rounded-lg border border-surface-2 bg-surface-2 p-3">
          <a
            href={e.url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm font-medium text-macro-accent hover:underline"
          >
            {e.title}
          </a>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-[10px] text-slate-500">
            <span>{e.domain}</span>
            <span>·</span>
            <span>{new Date(e.seendate).toLocaleString()}</span>
            <span>·</span>
            <span>tone {e.tone.toFixed(2)}</span>
          </div>
          {e.affected_assets.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1">
              {e.affected_assets.map((asset) => (
                <span key={asset} className="rounded bg-surface-1 px-1.5 py-0.5 text-[10px] text-slate-400">
                  {asset}
                </span>
              ))}
            </div>
          )}
        </li>
      ))}
    </ul>
  );
}

function CustomQueryBuilder() {
  const [query, setQuery] = useState("");
  const [enabled, setEnabled] = useState(false);
  const { data, isLoading, error } = useQuery({
    queryKey: ["geo", "custom", query],
    queryFn: () => fetchCustomGeo(query),
    enabled: enabled && query.length > 0,
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setEnabled(true);
  }

  return (
    <div className="rounded-xl border border-surface-2 bg-surface-1 p-4">
      <form onSubmit={handleSubmit} className="flex flex-col gap-2 sm:flex-row">
        <input
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setEnabled(false);
          }}
          placeholder='Try: (iran OR israel) "oil"'
          className="flex-1 rounded-lg border border-surface-2 bg-surface-0 px-3 py-2 text-sm text-slate-200 placeholder:text-slate-600 focus:border-macro-accent focus:outline-none"
          maxLength={500}
        />
        <button
          type="submit"
          disabled={!query || isLoading}
          className="rounded-lg bg-macro-accent px-4 py-2 text-sm font-medium text-white hover:bg-blue-600 disabled:opacity-50"
        >
          {isLoading ? "Running…" : "Run GDELT query"}
        </button>
      </form>
      <p className="mt-2 text-[10px] text-slate-500">
        Custom queries are cached for 1 hour. Uses GDELT query grammar: quotes for exact phrases, OR/AND, parentheses.
      </p>

      {error && <div className="mt-2 text-xs text-macro-down">Query failed. Try a simpler phrase.</div>}

      {data && !isLoading && (
        <div className="mt-3 rounded-lg border border-surface-2 bg-surface-0 p-3">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium">Custom query result</span>
            <span className="font-mono text-sm font-semibold">{data.value.toFixed(0)}/100</span>
          </div>
          <div className="text-[10px] text-slate-500">trend: {data.trend} · {data.top_events.length} stories</div>
        </div>
      )}
    </div>
  );
}
